import { db } from '../firebase'
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  Timestamp,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore'
import {
  loadOrderFinancials,
  frozenCommissionAmount,
  frozenCommissionRate,
} from './orderFinancials'

export interface FinancialSummary {
  totalRevenue: number
  totalCommissions: number
  totalDiscounts: number
  walletCharges: number
  walletWithdrawals: number
  netProfit: number
  commissionsByCategory: Record<string, number>
  paymentMethodsBreakdown: Record<string, number>
  topSellers: Array<{
    sellerId: string
    name: string
    revenue: number
    commission: number
  }>
  period: {
    start: Date
    end: Date
  }
}

export interface TransactionSummary {
  id: string
  type: string
  amount: number
  userId: string
  userName: string
  status: string
  createdAt: Date
  description?: string
}

export interface CommissionDetail {
  sellerId: string
  sellerName: string
  category: string
  orderValue: number
  commissionRate: number
  commissionAmount: number
  orderId: string
  createdAt: Date
}

/**
 * Get total revenue for a period
 */
export async function getTotalRevenue(startDate: Date, endDate: Date): Promise<number> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    // Get all completed payments
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    let totalRevenue = 0
    paymentsSnapshot.forEach(doc => {
      const data = doc.data()
      totalRevenue += data.amount || 0
    })
    
    return totalRevenue
  } catch (error) {
    console.error('Error getting total revenue:', error)
    throw error
  }
}

/**
 * Get total commissions for a period
 */
export async function getTotalCommissions(startDate: Date, endDate: Date): Promise<number> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    // Get all completed payments (commissions are stored in payments)
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    let totalCommissions = 0
    paymentsSnapshot.forEach(doc => {
      const data = doc.data()
      // Commission is the amount paid (before any discounts)
      totalCommissions += data.amount || 0
    })
    
    return totalCommissions
  } catch (error) {
    console.error('Error getting total commissions:', error)
    throw error
  }
}

/**
 * Get commissions breakdown by category
 */
export async function getCommissionsByCategory(
  startDate: Date, 
  endDate: Date
): Promise<Record<string, number>> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)

    // القيم المجمّدة — مصدر واحد ثابت بدل قراءة settings في كل تقرير
    const financials = await loadOrderFinancials()
    
    // Get all completed requests with their offers
    const requestsQuery = query(
      collection(db, 'requests'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const requestsSnapshot = await getDocs(requestsQuery)
    
    const commissionsByCategory: Record<string, number> = {}
    
    for (const reqDoc of requestsSnapshot.docs) {
      const reqData = reqDoc.data()
      
      // Only completed/received orders
      if (reqData.status === 'completed' || reqData.status === 'received') {
        // Get accepted offer for this request
        const offersQuery = query(
          collection(db, 'offers'),
          where('requestId', '==', reqDoc.id),
          where('status', '==', 'accepted')
        )
        const offersSnapshot = await getDocs(offersQuery)
        
        if (!offersSnapshot.empty) {
          const offerData = offersSnapshot.docs[0].data()
          const fin = financials.get(reqDoc.id)

          // نقرأ العمولة المجمّدة من orders/{requestId} فقط.
          // الطلبات القديمة غير المجمّدة تُترك خارج التقرير بدل حسابها
          // من نسب الإعدادات الحالية (وهي مصدر الخطأ السابق).
          const commission = frozenCommissionAmount(fin)
          if (commission == null) continue

          // الفئة من بيانات الطلب نفسها (لا من منتج في كتالوج يتغيّر)
          const category =
            (fin && (reqData.productCategory as string)) ||
            (reqData.productCategory as string) ||
            'other'

          commissionsByCategory[category] =
            (commissionsByCategory[category] || 0) + commission
        }
      }
    }
    
    return commissionsByCategory
  } catch (error) {
    console.error('Error getting commissions by category:', error)
    throw error
  }
}

/**
 * Get total coupon discounts for a period
 */
export async function getCouponDiscounts(startDate: Date, endDate: Date): Promise<number> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    // Get all payments with discount
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    let totalDiscounts = 0
    paymentsSnapshot.forEach(doc => {
      const data = doc.data()
      const discount = (data.amountBeforeDiscount || data.amount) - data.amount
      if (discount > 0) {
        totalDiscounts += discount
      }
    })
    
    return totalDiscounts
  } catch (error) {
    console.error('Error getting coupon discounts:', error)
    throw error
  }
}

/**
 * Get wallet transactions for a period
 */
export async function getWalletTransactions(
  startDate: Date, 
  endDate: Date
): Promise<{ charges: number; withdrawals: number; transactions: TransactionSummary[] }> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const transactionsQuery = query(
      collection(db, 'transactions'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      orderBy('createdAt', 'desc')
    )
    const transactionsSnapshot = await getDocs(transactionsQuery)
    
    let walletCharges = 0
    let walletWithdrawals = 0
    const transactions: TransactionSummary[] = []
    
    for (const transactionDoc of transactionsSnapshot.docs) {
      const data = transactionDoc.data()
      
      // Get user info
      let userName = 'Unknown'
      if (data.userId) {
        const userDocRef = doc(db, 'users', data.userId as string)
        const userDoc = await getDoc(userDocRef)
        if (userDoc.exists()) {
          userName = (userDoc.data() as { name?: string }).name || 'Unknown'
        }
      }
      
      transactions.push({
        id: transactionDoc.id,
        type: data.type || '',
        amount: data.amount || 0,
        userId: data.userId || '',
        userName,
        status: data.status || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        description: data.description || ''
      })
      
      if (data.type === 'charge' || data.type === 'deposit') {
        walletCharges += data.amount || 0
      } else if (data.type === 'withdrawal' || data.type === 'debit') {
        walletWithdrawals += data.amount || 0
      }
    }
    
    return {
      charges: walletCharges,
      withdrawals: walletWithdrawals,
      transactions
    }
  } catch (error) {
    console.error('Error getting wallet transactions:', error)
    throw error
  }
}

/**
 * Get net profit for a period
 */
export async function getNetProfit(startDate: Date, endDate: Date): Promise<number> {
  try {
    const totalCommissions = await getTotalCommissions(startDate, endDate)
    const totalDiscounts = await getCouponDiscounts(startDate, endDate)
    
    // Net profit = Commissions - Discounts
    // (In a real scenario, you might have other expenses to subtract)
    return totalCommissions - totalDiscounts
  } catch (error) {
    console.error('Error getting net profit:', error)
    throw error
  }
}

/**
 * Get payment methods breakdown
 */
export async function getPaymentMethodsBreakdown(
  startDate: Date, 
  endDate: Date
): Promise<Record<string, number>> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    const breakdown: Record<string, number> = {}
    
    paymentsSnapshot.forEach(doc => {
      const data = doc.data()
      const method = data.paymentMethod || 'unknown'
      breakdown[method] = (breakdown[method] || 0) + (data.amount || 0)
    })
    
    return breakdown
  } catch (error) {
    console.error('Error getting payment methods breakdown:', error)
    throw error
  }
}

/**
 * Get top sellers by revenue
 */
export async function getTopSellersByRevenue(
  limitCount: number, 
  startDate: Date, 
  endDate: Date
): Promise<Array<{ sellerId: string; name: string; revenue: number; commission: number }>> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)

    // القيم المجمّدة — لا نقرأ settings/commission_rates anymore
    const financials = await loadOrderFinancials()
    
    // Get all sellers
    const sellersQuery = query(
      collection(db, 'users'),
      where('type', '==', 'seller')
    )
    const sellersSnapshot = await getDocs(sellersQuery)
    
    const sellerRevenues: Record<string, { name: string; revenue: number; commission: number }> = {}
    
    // Calculate revenue for each seller
    for (const sellerDoc of sellersSnapshot.docs) {
      const sellerId = sellerDoc.id
      const sellerName = sellerDoc.data().name || 'Unknown'
      
      // Get accepted offers for this seller
      const offersQuery = query(
        collection(db, 'offers'),
        where('sellerId', '==', sellerId),
        where('status', '==', 'accepted'),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end)
      )
      const offersSnapshot = await getDocs(offersQuery)
      
      let totalRevenue = 0
      let totalCommission = 0
      
      for (const offerDoc of offersSnapshot.docs) {
        const offerData = offerDoc.data()
        
        // Check if the request is completed
        const requestId = offerData.requestId
        if (requestId) {
          const requestDocRef = doc(db, 'requests', requestId)
          const requestDoc = await getDoc(requestDocRef)
          
          if (requestDoc.exists()) {
            const reqData = requestDoc.data()
            if (reqData.status === 'completed' || reqData.status === 'received') {
              // الإيراد والعمولة من القيم المجمّدة لهذا الطلب.
              const fin = financials.get(requestId)
              const revenue = fin?.offerPrice ?? ((offerData.price as number) || 0)
              totalRevenue += revenue

              // العمولة المجمّدة فقط — لا يُشتق أي مبلغ من فئات التاجر الحالية
              const commission = frozenCommissionAmount(fin)
              if (commission != null) totalCommission += commission
            }
          }
        }
      }
      
      if (totalRevenue > 0) {
        sellerRevenues[sellerId] = {
          name: sellerName,
          revenue: totalRevenue,
          commission: totalCommission
        }
      }
    }
    
    // Sort by revenue and take top N
    const sortedSellers = Object.entries(sellerRevenues)
      .map(([sellerId, data]) => ({
        sellerId,
        ...data
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, limitCount)
    
    return sortedSellers
  } catch (error) {
    console.error('Error getting top sellers by revenue:', error)
    throw error
  }
}

/**
 * Get comprehensive financial summary
 */
export async function getFinancialSummary(
  startDate: Date, 
  endDate: Date
): Promise<FinancialSummary> {
  try {
    const [
      totalRevenue,
      totalCommissions,
      totalDiscounts,
      walletData,
      netProfit,
      commissionsByCategory,
      paymentMethodsBreakdown,
      topSellers
    ] = await Promise.all([
      getTotalRevenue(startDate, endDate),
      getTotalCommissions(startDate, endDate),
      getCouponDiscounts(startDate, endDate),
      getWalletTransactions(startDate, endDate),
      getNetProfit(startDate, endDate),
      getCommissionsByCategory(startDate, endDate),
      getPaymentMethodsBreakdown(startDate, endDate),
      getTopSellersByRevenue(10, startDate, endDate)
    ])
    
    return {
      totalRevenue,
      totalCommissions,
      totalDiscounts,
      walletCharges: walletData.charges,
      walletWithdrawals: walletData.withdrawals,
      netProfit,
      commissionsByCategory,
      paymentMethodsBreakdown,
      topSellers,
      period: { start: startDate, end: endDate }
    }
  } catch (error) {
    console.error('Error getting financial summary:', error)
    throw error
  }
}

/**
 * Get detailed commission records
 */
export async function getCommissionDetails(
  startDate: Date, 
  endDate: Date
): Promise<CommissionDetail[]> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)

    // القيم المجمّدة — مصدر واحد للعمولة
    const financials = await loadOrderFinancials()
    
    // Get all completed requests
    const requestsQuery = query(
      collection(db, 'requests'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const requestsSnapshot = await getDocs(requestsQuery)
    
    const commissionDetails: CommissionDetail[] = []
    
    for (const reqDoc of requestsSnapshot.docs) {
      const reqData = reqDoc.data()
      
      if (reqData.status === 'completed' || reqData.status === 'received') {
        // العمولة تُقرأ من orders/{requestId} فقط.
        // الطلبات القديمة غير المجمّدة تُتخطى بدل عرض نسبة محسوبة من الإعدادات.
        const fin = financials.get(reqDoc.id)
        const commissionAmount = frozenCommissionAmount(fin)
        const commissionRate = frozenCommissionRate(fin)
        if (commissionAmount == null || commissionRate == null) continue

        // Get accepted offer
        const offersQuery = query(
          collection(db, 'offers'),
          where('requestId', '==', reqDoc.id),
          where('status', '==', 'accepted')
        )
        const offersSnapshot = await getDocs(offersQuery)
        
        if (!offersSnapshot.empty) {
          const offerData = offersSnapshot.docs[0].data()
          const sellerId = fin?.sellerId || offerData.sellerId
          
          // Get seller info
          const sellerDocRef = doc(db, 'users', sellerId)
          const sellerDoc = await getDoc(sellerDocRef)
          
          if (sellerDoc.exists()) {
            const sellerName = sellerDoc.data().name || 'Unknown'
            // الفئة كما سُجّلت وقت الطلب، لا من فئات التاجر الحالية
            const category = (reqData.productCategory as string) || 'other'
            
            commissionDetails.push({
              sellerId,
              sellerName,
              category,
              orderValue: fin?.offerPrice ?? (offerData.price || 0),
              commissionRate,
              commissionAmount,
              orderId: reqDoc.id,
              createdAt: reqData.createdAt?.toDate() || new Date()
            })
          }
        }
      }
    }
    
    return commissionDetails.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  } catch (error) {
    console.error('Error getting commission details:', error)
    throw error
  }
}

/**
 * Get used coupons for a period
 */
export async function getUsedCoupons(startDate: Date, endDate: Date) {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    const usedCoupons = []
    
    for (const paymentDoc of paymentsSnapshot.docs) {
      const data = paymentDoc.data()
      
      if (data.couponId) {
        // Get coupon details
        const couponDocRef = doc(db, 'coupons', data.couponId as string)
        const couponDoc = await getDoc(couponDocRef)
        
        if (couponDoc.exists()) {
          const couponData = couponDoc.data() as { code?: string }
          
          usedCoupons.push({
            paymentId: paymentDoc.id,
            couponId: data.couponId as string,
            couponCode: couponData.code || '',
            discountAmount: (data.amountBeforeDiscount || data.amount) - data.amount,
            userId: data.userId || '',
            createdAt: data.createdAt?.toDate() || new Date()
          })
        }
      }
    }
    
    return usedCoupons
  } catch (error) {
    console.error('Error getting used coupons:', error)
    throw error
  }
}

/**
 * Get financial time series data for charts
 */
export async function getFinancialTimeSeriesData(
  startDate: Date,
  endDate: Date,
  interval: 'daily' | 'weekly' | 'monthly' = 'daily'
) {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'completed'),
      orderBy('createdAt', 'asc')
    )
    const paymentsSnapshot = await getDocs(paymentsQuery)
    
    const dataPoints: Record<string, { revenue: number; commissions: number; discounts: number }> = {}
    
    paymentsSnapshot.forEach(doc => {
      const data = doc.data()
      const date = data.createdAt?.toDate()
      if (!date) return
      
      const key = formatDateByInterval(date, interval)
      
      if (!dataPoints[key]) {
        dataPoints[key] = { revenue: 0, commissions: 0, discounts: 0 }
      }
      
      const amount = data.amount || 0
      const discount = (data.amountBeforeDiscount || amount) - amount
      
      dataPoints[key].revenue += amount
      dataPoints[key].commissions += amount
      dataPoints[key].discounts += discount
    })
    
    return Object.entries(dataPoints).map(([date, data]) => ({
      date,
      ...data
    }))
  } catch (error) {
    console.error('Error getting financial time series data:', error)
    throw error
  }
}

/**
 * Format date by interval
 */
function formatDateByInterval(date: Date, interval: 'daily' | 'weekly' | 'monthly'): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  switch (interval) {
    case 'daily':
      return `${year}-${month}-${day}`
    case 'weekly':
      const weekNum = getWeekNumber(date)
      return `${year}-W${weekNum}`
    case 'monthly':
      return `${year}-${month}`
    default:
      return `${year}-${month}-${day}`
  }
}

/**
 * Get week number
 */
function getWeekNumber(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return String(weekNo).padStart(2, '0')
}

